import { useState } from 'react';
import { Alert, Stack, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { platformAuthApi } from '@/api/platform.api';
import AuthShell from '@/components/layout/AuthShell';
import { storage } from '@/lib/storage';
import { platformPath } from '@/config/appTarget';
import { hasPlatformAccess } from '@/auth/platformAccess';
import SubmitButton from '@/components/common/SubmitButton';
import { humanizeError } from '@/utils/errorMessage';

const NO_ACCESS = 'This account does not have Platform Console access.';

/** Platform (SaaS owner) sign-in — POST /api/platform/auth/login. */
export default function PlatformLoginPage() {
  const navigate = useNavigate();
  // Sent back by the console shell: the session held no platform role, or the server refused it.
  const deniedOnArrival = (useLocation().state as { denied?: boolean } | null)?.denied === true;
  const [error, setError] = useState<string | null>(deniedOnArrival ? NO_ACCESS : null);
  const { control, handleSubmit, formState } = useForm({ defaultValues: { email: '', password: '' } });

  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    try {
      const res = await platformAuthApi.login({ email: v.email.trim(), password: v.password });
      storage.setSession(res);
      // Signed in is not the same as allowed in: only a platform role opens the console.
      const profile = await platformAuthApi.profile();
      if (!hasPlatformAccess(profile)) {
        storage.clearSession();
        setError(NO_ACCESS);
        return;
      }
      navigate(platformPath('/dashboard'), { replace: true });
    } catch (e) {
      setError(humanizeError(e, 'Sign in failed.'));
    }
  });

  return (
    <AuthShell productBranded title="Platform Console" subtitle="GoERP team sign in.">
      <form onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Controller name="email" control={control} rules={{ required: 'Email is required' }} render={({ field, fieldState }) => (
            <TextField {...field} label="Email" type="email" autoComplete="username" autoFocus fullWidth error={!!fieldState.error} helperText={fieldState.error?.message} />
          )} />
          <Controller name="password" control={control} rules={{ required: 'Password is required' }} render={({ field, fieldState }) => (
            <TextField {...field} label="Password" type="password" autoComplete="current-password" fullWidth error={!!fieldState.error} helperText={fieldState.error?.message} />
          )} />
          <SubmitButton size="large" fullWidth loading={formState.isSubmitting} loadingText="Signing in…">
            Sign in
          </SubmitButton>
        </Stack>
      </form>
    </AuthShell>
  );
}
